import { useLocation, useNavigate } from 'react-router-dom'

import {
  AppBar,
  Box,
  Chip,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ArrowBack,
  Biotech,
  Description,
  Home,
  Info,
  LocalHospital,
  PlaylistAddCheck,
  PriceChange,
  QrCodeScanner,
  Settings,
  Warehouse,
} from '@mui/icons-material'

import packageJson from '../../../package.json'
import {
  appBar,
  MenuButtonIconProps,
} from '../../garvan-react/Constants/StyleConstants'
import { useUserContext } from '../../Contexts/UserConfig'
import { useHoldingContext } from '../../Contexts/CurrentHolding'

function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentHolding } = useHoldingContext()
  const { whoAmI, setWhoAmI, loginConfig } = useUserContext()

  function logout() {
    setWhoAmI(undefined)
    if (
      ['development', 'production', undefined].includes(
        process.env.REACT_APP_STAGE,
      )
    ) {
      navigate(
        `/?saml_slo=true&homeurl=${window.location.origin}%2F&loginlogout=Log+Out`,
      )
    }
    window.location.reload()
  }

  if (whoAmI?.token) {
    return (
      <AppBar
        sx={{
          height: appBar,
          position: 'fixed',
          p: 1 / 5,
          justifyContent: 'center',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{ display: 'flex', flexDirection: 'row', px: '10px !important' }}
        >
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' } }}>
            <Tooltip title="Back">
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: 'primary.contrastText',
                  mr: 2,
                  display: { sm: 'none', md: 'flex' },
                }}
              >
                <ArrowBack sx={MenuButtonIconProps} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Return home to StuartWeb">
              <span>
                <IconButton
                  onClick={() => {
                    navigate('/')
                    window.location.reload()
                  }}
                  sx={{ color: 'primary.contrastText' }}
                >
                  <Home sx={MenuButtonIconProps} />
                </IconButton>
              </span>
            </Tooltip>
            {loginConfig?.Modules?.ShowReaderModule && (
              <Tooltip title="Code Reader">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/reader')}
                    sx={{ color: 'primary.contrastText' }}
                    disabled={location.pathname.startsWith('/stuart/reader')}
                  >
                    <QrCodeScanner sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loginConfig?.Modules?.ShowSamplesModule && (
              <Tooltip title="Samples">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/sample/collect')}
                    sx={{ color: 'primary.contrastText' }}
                    disabled={location.pathname.startsWith('/stuart/sample')}
                  >
                    <PlaylistAddCheck sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loginConfig?.Modules?.ShowScreenModule && (
              <Tooltip title="Health">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/health/complete')}
                    sx={{ color: 'primary.contrastText' }}
                    disabled={location.pathname.startsWith('/stuart/health')}
                  >
                    <LocalHospital sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loginConfig?.Modules?.ShowStudiesModule && (
              <Tooltip title="Studies">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/study/protocols')}
                    sx={{
                      color:
                        location.pathname.startsWith('/stuart/study') ?
                          'primary.dark'
                        : 'primary.contrastText',
                    }}
                  >
                    <Biotech sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loginConfig?.Modules?.ShowBioBankModule && (
              <Tooltip title="Bio Bank">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/bio-bank')}
                    sx={{ color: 'primary.contrastText' }}
                    disabled={location.pathname.startsWith('/stuart/bio-bank')}
                  >
                    <Warehouse sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loginConfig?.Modules?.ShowReportsModule && (
              <Tooltip title="Reports">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/report')}
                    sx={{ color: 'primary.contrastText' }}
                    disabled={location.pathname.startsWith('/stuart/report')}
                  >
                    <Description sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {loginConfig?.Modules?.ShowPricebook && (
              <Tooltip title="Pricebook">
                <span>
                  <IconButton
                    onClick={() => navigate('/stuart/pricebook')}
                    sx={{ color: 'primary.contrastText' }}
                    disabled={location.pathname.startsWith('/stuart/pricebook')}
                  >
                    <PriceChange sx={MenuButtonIconProps} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          {(location.pathname.startsWith('/stuart/sample') ||
            location.pathname.startsWith('/stuart/health') ||
            location.pathname.startsWith('/stuart/reader')) && (
            <Tooltip title="Change Location">
              <Chip
                sx={{
                  py: 2,
                  px: 1,
                  borderWidth: 1,
                  borderColor: 'primary.contrastText',
                }}
                label={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}
                  >
                    {currentHolding?.name}
                  </Typography>
                }
                variant={'outlined'}
                onClick={() => navigate('/stuart/location')}
              />
            </Tooltip>
          )}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Tooltip title="Settings">
              <IconButton
                name="settings"
                onClick={() => {
                  navigate('/stuart/settings')
                }}
                sx={{ color: 'primary.contrastText' }}
              >
                <Settings sx={MenuButtonIconProps} />
              </IconButton>
            </Tooltip>
            <Tooltip title="About">
              <IconButton
                name="info"
                onClick={() => {
                  navigate('/stuart/About')
                }}
                sx={{
                  color: 'primary.contrastText',
                  display: { sm: 'none', md: 'flex' },
                }}
              >
                <Info sx={MenuButtonIconProps} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout">
              <Chip
                sx={{
                  p: 1 / 2,
                  borderWidth: 2,
                  borderColor: 'primary.contrastText',
                }}
                onClick={() => logout()}
                label={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}
                  >
                    {whoAmI.user}
                  </Typography>
                }
                variant={'outlined'}
              />
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
    )
  } else {
    return (
      <AppBar
        sx={{
          height: appBar,
          position: 'fixed',
          p: 1 / 5,
          justifyContent: 'center',
        }}
      >
        <Toolbar>
          <Box sx={{ alignItems: 'center', alignContent: 'center', width: 1 }}>
            <Typography
              variant="h6"
              sx={{ textAlign: 'center', fontWeight: 'bold' }}
            >
              {packageJson.shortName}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
    )
  }
}

export default Header
